import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { LoadOptions } from 'devextreme/data';
import { ResolvedData } from 'devextreme/data/custom_store';
import { Observable, filter, lastValueFrom, map } from 'rxjs';

import { CoreModuleName } from '@app/core/models/core-constant.model';
import { EidosConfigService } from '@common/config/eidos-config.service';
import { IEidosModuleConfiguration } from '@common/config/environment.interface';
import { DynamicApiResponse } from '@common/models/dynamic-api-response.model';
import { CoreFormatService } from '@common/services/core-format.service';
import { EidosBaseApiService } from '@common/services/eidos-base-api.service';
import { EidosLogService } from '@common/services/eidos-log.service';
import { EidosSecurityService } from '@common/services/eidos-security.service';

import { IReservationModuleAPIConfiguration } from '@app/res-common/models/rescom.model';
import { IResApiCompareManifestParams, IResApiCompareTwoManifestsParams, IResApiDeleteFullManifestParams, IResApiDeleteManifestParams, IResApiGetManifestParams, IResApiGetSavedFullManifestsParams, IResApiGetSavedManifestsParams, IResApiOpenSavedFullManifestParams, IResApiOpenSavedManifestParams, IResApiSaveFullManifestParams, IResApiSaveManifestParams, IResApiUpdateFullManifestHeaderParams, IResApiUpdateFullManifestParams, IResApiUpdateManifestHeaderParams, IResApiUpdateManifestParams, ResManifest, ResSavedManifestHeader } from '../models/res-manifest.model';
import { IResApiGetReplaceOptionsHistoryParams, IResApiReplaceAvailability, IResApiReplaceOptionsParams, IResApiReplaceStatus, ResReplaceHistory } from '../models/res-option.models';

@Injectable({
  providedIn: 'root'
})
export class ReservationManifestApiService extends EidosBaseApiService {

  constructor(
    public configService: EidosConfigService,
    public eidosSecurityService: EidosSecurityService,
    public http: HttpClient,
    public eidosLogService: EidosLogService,
    public domSanitizer: DomSanitizer,
    public coreFormatService: CoreFormatService
  ) {
    super(coreFormatService, configService, eidosLogService, eidosSecurityService, http);
    this.configService.currentModulesConfig
      .pipe(
        filter(
          (modules: Array<IEidosModuleConfiguration>) => !!modules.find((module) => module.moduleName === CoreModuleName.Reservation)
        )
      )
      .subscribe((modules) => {
        const module = modules.find((module) => module.moduleName === CoreModuleName.Reservation);
        if (module) {
          const reservationModuleAPI = module.API as IReservationModuleAPIConfiguration;
          this.urlAPI = reservationModuleAPI.url;
          this.staticUrlAPI = reservationModuleAPI.staticUrl;
          this.pluginUrlAPI = reservationModuleAPI.pluginUrl;
          this.apiVersion = reservationModuleAPI.version;
          if (reservationModuleAPI.headers) {
            this.headers = {
              ...reservationModuleAPI.headers,
              ...this.headers
            };
          }
        }
      });
  }

  private fillPaginatedParams(params?: any, loadOptions?: LoadOptions) {
    if (!params) params = {};
    if (!loadOptions) return params;

    // Paginazione
    const skip = loadOptions.skip ?? 0;
    const take = loadOptions.take ?? 20;
    params.PageNum = Math.floor(skip / take) + 1;
    params.PageSize = take;

    // Filtri
    if (loadOptions.filter) {
      const filters = Array.isArray(loadOptions.filter[0])
        ? loadOptions.filter
        : [loadOptions.filter];

      filters.forEach((filter: any) => {
        if (Array.isArray(filter)) {
          const [field, _operator, value] = filter;
          params[field] = value;  // Nome più chiaro
        }
      });
    }

    // Ordinamento
    if (loadOptions.sort) {
      const sort = loadOptions.sort as { selector: string; desc: boolean }[];
      params.OrderField = sort[0]?.selector;
      params.OrderDirect = sort[0]?.desc ? 1 : 0;
    }

    return params;
  }

  public checkReplaceServiceOptionsAvl(params: IResApiReplaceOptionsParams): Observable<IResApiReplaceAvailability[]> {
    return this.postDynamicAPI('option/replaceCheckAvl', params).pipe(
      map<DynamicApiResponse, Array<IResApiReplaceAvailability>>((response) => {
        return response.body;
      })
    );
  }

  compareManifest(params: IResApiCompareManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('manifest/compare', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body?.map?.((c: any) => new ResManifest(c)) ?? [],
          totalCount: response.recordsCount
        }
      })
    );
  }

  compareTwoManifest(params: IResApiCompareTwoManifestsParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('manifest/compareTwo', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body?.map?.((c: any) => new ResManifest(c)) ?? [],
          totalCount: response.recordsCount
        }
      })
    );
  }

  deleteManifest(params: IResApiDeleteManifestParams | IResApiDeleteFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    const url = isFullManifest ? 'fullManifest/delete' : 'manifest/delete';
    return this.postDynamicAPI(url, params);
  }

  getManifest(params: IResApiGetManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);
    const url = params.PackageID && params.PackageID > 0 ? 'manifest/getManifestPkg' : 'manifest/select';
    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResManifest(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getManifestGroupBy(params: IResApiGetManifestParams, loadOptions: any): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);
    const url = params.PackageID && params.PackageID > 0 ? 'manifest/getManifestGroupPkg' : 'manifestGroupBy/select';
    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResManifest(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getManifestGroupByPaginatedPKG(params: IResApiGetManifestParams, loadOptions?: any): Promise<ResolvedData<ResManifest[]>> {
    params = this.fillPaginatedParams(params, loadOptions);
    return lastValueFrom(this.callDynamicAPI('manifest/getManifestProductReplace', params)).then((response: any) => {
      return {
        data: response.body.map((manifestGroup: any) => new ResManifest(manifestGroup)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  getManifestGroupByPaginatedPR(params: IResApiGetManifestParams, loadOptions?: any): Promise<ResolvedData<ResManifest[]>> {
    params = this.fillPaginatedParams(params, loadOptions);
    const isPackageSearch = (params.PackageID && params.PackageID > 0) || (params.BkgID && params.BkgID > 0) || (params.ServiceName && params.ServiceName.trim() !== '');
    const url = isPackageSearch ? 'manifest/getManifestGroupPkgPR' : 'manifestGroupBy/selectPR';
    return lastValueFrom(this.callDynamicAPI(url, params)).then((response: any) => {
      return {
        data: response.body.map((manifestGroup: any) => new ResManifest(manifestGroup)),
        totalCount: response.recordsCount
      };
    }).catch(() => {
      throw 'Data loading error'
    });
  }

  getManifestPackageServiceTypes(): Observable<{ serviceTypeID: number, serviceTypeName: string, productCod: string }[]> {
    return this.callDynamicAPI('package/getServiceTypePkg').pipe(
      map<DynamicApiResponse, { serviceTypeID: number, serviceTypeName: string, productCod: string }[]>(
        (response) => response.body.map((data: any) => ({ serviceTypeID: data.ServiceTypeID, serviceTypeName: data.ServiceTypeName, productCod: data.ProductCod }))
      ));
  }

  public getReplaceServiceOptionsHistory(params: IResApiGetReplaceOptionsHistoryParams, loadOptions: any): Observable<{ data: ResReplaceHistory[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    return this.callDynamicAPI('option/replaceHistory', params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResReplaceHistory[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResReplaceHistory(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  getSavedManifest(params: IResApiGetSavedManifestsParams | IResApiGetSavedFullManifestsParams, loadOptions: any, isFullManifest: boolean = false): Observable<{ data: ResSavedManifestHeader[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    let url = ''
    if (params.PackageID && params.PackageID > 0) {
      url = 'manifest/getManifestSavedGetPkg'
    } else {
      url = isFullManifest ? 'fullManifestSaved/select' : 'manifestSaved/select'
    }

    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResSavedManifestHeader[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResSavedManifestHeader(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  openSavedManifest(params: IResApiOpenSavedManifestParams | IResApiOpenSavedFullManifestParams, loadOptions: any, isFullManifest: boolean = false): Observable<{ data: ResManifest[], totalCount: number }> {
    params = this.fillPaginatedParams(params, loadOptions);

    let url = ''
    if (params.PackageID && params.PackageID > 0) {
      url = 'manifest/getManifestSavedOpenPkg'
    } else {
      url = isFullManifest ? 'fullManifestSaved/open' : 'manifestSaved/open'
    }

    return this.callDynamicAPI(url, params, 'GET').pipe(
      map<DynamicApiResponse, { data: ResManifest[], totalCount: number }>((response) => {
        return {
          data: response.body.map((c: any) => new ResManifest(c)),
          totalCount: response.recordsCount
        }
      })
    );
  }

  public replaceServiceOptions(params: IResApiReplaceOptionsParams): Observable<IResApiReplaceStatus[]> {
    return this.postDynamicAPI('option/replace', params).pipe(
      map<DynamicApiResponse, Array<IResApiReplaceStatus>>((response) => {
        const { body } = response;
        return !body || Array.isArray(body) ? body : body[Object.keys(body).at(-1) ?? -1] ?? [];
      })
    );
  }

  saveManifest(params: IResApiSaveManifestParams | IResApiSaveFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    if (params.PackageID && params.PackageID > 0) {
      const url = 'manifest/getManifestSaveAsPkg';
      return this.postDynamicAPI(url, params);
    } else {
      const url = isFullManifest ? 'fullManifest/save' : 'manifest/save';
      return this.postDynamicAPI(url, params);
    }
  }

  updateManifest(params: IResApiUpdateManifestParams | IResApiUpdateFullManifestParams, isFullManifest: boolean = false): Observable<any> {
    const url = isFullManifest ? 'fullManifest/update' : 'manifest/update';
    return this.postDynamicAPI(url, params);
  }

  updateManifestHeader(params: IResApiUpdateManifestHeaderParams | IResApiUpdateFullManifestHeaderParams, isFullManifest: boolean = false): Observable<any> {
    const url = isFullManifest ? 'fullManifestHdr/update' : 'manifestHdr/update';
    return this.postDynamicAPI(url, params);
  }
}
